import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  InputAdornment
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import apiService from '../../services/apiService';

// Esquema de validación
const ShipmentSchema = Yup.object().shape({
  branch_origin: Yup.number().required('La sucursal de origen es requerida'),
  branch_destiny: Yup.number().required('La sucursal de destino es requerida'),
  currency_type: Yup.string().required('El tipo de moneda es requerido'),
  amount: Yup.number()
    .required('El monto es requerido')
    .positive('El monto debe ser positivo')
    .min(10, 'El monto mínimo es 10'),
  comments: Yup.string()
});

const ShipmentForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [branches, setBranches] = useState([]);

  // Valores iniciales para el formulario
  const initialValues = {
    branch_origin: '',
    branch_destiny: '',
    currency_type: 'PEN',
    amount: '',
    comments: ''
  };

  // Cargar datos necesarios
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Cargar sucursales
        const branchesData = await apiService.getBranches();
        setBranches(branchesData.filter(branch => branch.is_enabled));
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError('Error al cargar los datos necesarios. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError(null);
      
      // Crear envío de dinero
      const newShipment = await apiService.createMoneyShipment(values);
      
      // Navegar a la vista de detalle
      navigate(`/shipments/view/${newShipment.id}`);
    } catch (err) {
      console.error('Error al crear envío de dinero:', err);
      setError('Error al crear el envío de dinero. Por favor, intente nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/shipments');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          Volver
        </Button>
        <Typography variant="h4">
          Nuevo Envío de Dinero
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={ShipmentSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting, values }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información de Sucursales
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={touched.branch_origin && Boolean(errors.branch_origin)}>
                    <InputLabel id="branch-origin-label">Sucursal de Origen</InputLabel>
                    <Field
                      as={Select}
                      name="branch_origin"
                      labelId="branch-origin-label"
                      label="Sucursal de Origen"
                    >
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.branch_name}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.branch_origin && errors.branch_origin && (
                      <FormHelperText>{errors.branch_origin}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={touched.branch_destiny && Boolean(errors.branch_destiny)}>
                    <InputLabel id="branch-destiny-label">Sucursal de Destino</InputLabel>
                    <Field
                      as={Select}
                      name="branch_destiny"
                      labelId="branch-destiny-label"
                      label="Sucursal de Destino"
                    >
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.branch_name}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.branch_destiny && errors.branch_destiny && (
                      <FormHelperText>{errors.branch_destiny}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información del Envío
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={touched.currency_type && Boolean(errors.currency_type)}>
                    <InputLabel id="currency-type-label">Moneda</InputLabel>
                    <Field
                      as={Select}
                      name="currency_type"
                      labelId="currency-type-label"
                      label="Moneda"
                    >
                      <MenuItem value="PEN">Soles (PEN)</MenuItem>
                      <MenuItem value="USD">Dólares (USD)</MenuItem>
                    </Field>
                    {touched.currency_type && errors.currency_type && (
                      <FormHelperText>{errors.currency_type}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="amount"
                    label="Monto"
                    fullWidth
                    variant="outlined"
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {values.currency_type === 'PEN' ? 'S/.' : '$'}
                        </InputAdornment>
                      ),
                    }}
                    error={touched.amount && Boolean(errors.amount)}
                    helperText={touched.amount && errors.amount}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="comments"
                    label="Comentarios"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                    error={touched.comments && Boolean(errors.comments)}
                    helperText={touched.comments && errors.comments}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      sx={{ mr: 1 }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {isSubmitting ? 'Guardando...' : 'Crear Envío'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default ShipmentForm;